import type { GitHunk } from "gitpod-next-api/supervisor/v1/environmentopts_pb";
import { cva } from "class-variance-authority";
import React, { useCallback, useMemo, useState, useEffect, useRef, type FC } from "react";
import { cn } from "@/components/podkit/lib/cn";
import { IconInfo } from "@/assets/icons/geist/IconInfo";
import { Tooltip } from "@/components/Tooltip";
import { Button } from "@/components/flexkit/Button";
import type { ReviewComment } from "@/components/ai/review/types";
import { LINE_MARKER_ADDED, LINE_MARKER_REMOVED, LINE_MARKER_UNCHANGED } from "@/routes/environments/code-changes/diff-utils";

enum LineChangeKind {
    Added = "Added",
    Removed = "Removed",
    Unchanged = "Unchanged",

    /**
     * A line that represents a break between hunks
     */
    Break = "Break",
}

type LineChange = {
    kind: LineChangeKind;
    key: string;
    originalLineNumber: number;
    newLineNumber: number;
    line: string;
    /** Index in the array for selection purposes */
    index: number;
};

export type LineSelection = {
    startLine: number;
    endLine: number;
    /** Index range for visual highlighting */
    startIndex: number;
    endIndex: number;
};

const lineContentClass = cva("px-2 flex-1 text-sm", {
    variants: {
        kind: {
            [LineChangeKind.Added]: "bg-surface-success-subtle text-content-success",
            [LineChangeKind.Removed]: "bg-surface-destructive-subtle text-content-destructive",
            [LineChangeKind.Unchanged]: "bg-surface-secondary-subtle text-content-secondary",
            [LineChangeKind.Break]: "bg-surface-muted",
        },
        selected: {
            true: "bg-surface-tertiary",
            false: "",
        },
        hasComment: {
            true: "bg-surface-warning-subtle",
            false: "",
        },
    },
});

const lineNumberClass = cva("px-2 w-full justify-self-center text-end border-r border-r-border-base text-sm select-none font-mono cursor-pointer", {
    variants: {
        kind: {
            [LineChangeKind.Added]: "bg-surface-success-subtle text-content-success",
            [LineChangeKind.Removed]: "bg-surface-destructive-subtle text-content-destructive",
            [LineChangeKind.Unchanged]: "bg-surface-secondary text-content-secondary",
            [LineChangeKind.Break]: "bg-surface-secondary text-content-secondary border-r-0 cursor-default",
        },
        selected: {
            true: "bg-surface-tertiary",
            false: "",
        },
        hasComment: {
            true: "bg-surface-warning-subtle",
            false: "",
        },
    },
});

export type GitDiffViewProps = {
    hunks: GitHunk[];
    /** Enable line selection for commenting */
    selectable?: boolean;
    /** Callback when user selects lines for a comment */
    onLineSelect?: (selection: LineSelection) => void;
    /** Comments for this file to show indicators */
    comments?: ReviewComment[];
    /** Callback when user clicks on a comment indicator */
    onCommentClick?: (comment: ReviewComment) => void;
    /** Currently selected lines for adding a comment (shows inline form) */
    pendingCommentSelection?: { startLine: number; endLine: number } | null;
    /** Callback to add a comment */
    onAddComment?: (content: string) => void;
    /** Callback to cancel adding a comment */
    onCancelComment?: () => void;
};

const GitDiffView: FC<GitDiffViewProps> = ({ hunks, selectable = false, onLineSelect, comments = [], onCommentClick, pendingCommentSelection, onAddComment, onCancelComment }) => {
    const [currentSelection, setCurrentSelection] = useState<LineSelection | null>(null);
    const [selectionStart, setSelectionStart] = useState<{ index: number; lineNumber: number } | null>(null);
    const [isDragging, setIsDragging] = useState(false);

    // Build a map of line numbers to comments for quick lookup
    const lineToComments = useMemo(() => {
        const map = new Map<number, ReviewComment[]>();
        for (const comment of comments) {
            for (let line = comment.startLine; line <= comment.endLine; line++) {
                const existing = map.get(line) || [];
                if (!existing.includes(comment)) {
                    existing.push(comment);
                }
                map.set(line, existing);
            }
        }
        return map;
    }, [comments]);

    const getCommentsForLine = useCallback((lineNumber: number) => {
        return lineToComments.get(lineNumber) || [];
    }, [lineToComments]);

    const codeLines = useMemo(() => {
        const lineChanges: LineChange[] = [];
        let index = 0;
        for (let i = 0; i < hunks.length; i++) {
            const hunk = hunks[i];
            const lines = hunk.body.split("\n");
            let originalLineOffset = -1;
            let newLineOffset = -1;
            for (const line of lines) {
                if (line === "") continue;
                let changeKind: LineChangeKind;
                switch (line[0]) {
                    case LINE_MARKER_ADDED:
                        changeKind = LineChangeKind.Added;
                        newLineOffset += 1;
                        break;
                    case LINE_MARKER_REMOVED:
                        changeKind = LineChangeKind.Removed;
                        originalLineOffset += 1;
                        break;
                    case LINE_MARKER_UNCHANGED:
                        changeKind = LineChangeKind.Unchanged;
                        originalLineOffset += 1;
                        newLineOffset += 1;
                        break;
                    default:
                        throw new Error(`Invalid line marker: ${line[0]}`);
                }
                const originalLineNumber = originalLineOffset + hunk.originalStartLine;
                const newLineNumber = newLineOffset + hunk.newStartLine;
                const key = `${changeKind}-${originalLineNumber}-${newLineNumber}`;
                lineChanges.push({
                    key,
                    originalLineNumber,
                    newLineNumber,
                    kind: changeKind,
                    line: line.slice(1),
                    index: index++,
                });
            }

            if (i !== hunks.length - 1) {
                lineChanges.push({
                    key: `break-${hunk.originalStartLine}-${hunk.newStartLine}`,
                    originalLineNumber: 0,
                    newLineNumber: 0,
                    kind: LineChangeKind.Break,
                    line: "",
                    index: index++,
                });
            }
        }
        return lineChanges;
    }, [hunks]);

    // Handle mouse up globally to end drag selection
    useEffect(() => {
        const handleMouseUp = () => {
            if (isDragging && currentSelection && onLineSelect) {
                onLineSelect(currentSelection);
                setCurrentSelection(null);
                setSelectionStart(null);
            }
            setIsDragging(false);
        };

        if (isDragging) {
            document.addEventListener("mouseup", handleMouseUp);
            return () => document.removeEventListener("mouseup", handleMouseUp);
        }
    }, [isDragging, currentSelection, onLineSelect]);

    const handleLineMouseDown = useCallback(
        (line: LineChange, event: React.MouseEvent) => {
            if (!selectable || line.kind === LineChangeKind.Break) return;
            
            // Prevent text selection during drag
            event.preventDefault();

            const lineNumber = line.kind === LineChangeKind.Removed ? line.originalLineNumber : line.newLineNumber;

            setSelectionStart({ index: line.index, lineNumber });
            setCurrentSelection({ startLine: lineNumber, endLine: lineNumber, startIndex: line.index, endIndex: line.index });
            setIsDragging(true);
        },
        [selectable],
    );

    const handleLineMouseEnter = useCallback(
        (line: LineChange) => {
            if (!isDragging || !selectionStart || line.kind === LineChangeKind.Break) return;

            const lineNumber = line.kind === LineChangeKind.Removed ? line.originalLineNumber : line.newLineNumber;

            const startLine = Math.min(selectionStart.lineNumber, lineNumber);
            const endLine = Math.max(selectionStart.lineNumber, lineNumber);
            const startIndex = Math.min(selectionStart.index, line.index);
            const endIndex = Math.max(selectionStart.index, line.index);

            setCurrentSelection({ startLine, endLine, startIndex, endIndex });
        },
        [isDragging, selectionStart],
    );

    const handleLineClick = useCallback(
        (line: LineChange, _event: React.MouseEvent) => {
            // Single click without drag - select single line and trigger callback
            if (!selectable || line.kind === LineChangeKind.Break) return;
            if (isDragging) return; // Don't handle click if we were dragging

            const lineNumber = line.kind === LineChangeKind.Removed ? line.originalLineNumber : line.newLineNumber;
            const newSelection = { startLine: lineNumber, endLine: lineNumber, startIndex: line.index, endIndex: line.index };

            if (onLineSelect) {
                onLineSelect(newSelection);
            }
        },
        [selectable, isDragging, onLineSelect],
    );

    const isLineSelected = useCallback(
        (index: number) => {
            if (!currentSelection) return false;
            return index >= currentSelection.startIndex && index <= currentSelection.endIndex;
        },
        [currentSelection],
    );

    // State for inline comment form
    const [inlineCommentContent, setInlineCommentContent] = useState("");

    // Reset inline comment content when selection changes
    useEffect(() => {
        setInlineCommentContent("");
    }, [pendingCommentSelection?.startLine, pendingCommentSelection?.endLine]);

    const handleInlineCommentSubmit = useCallback(() => {
        if (inlineCommentContent.trim() && onAddComment) {
            onAddComment(inlineCommentContent.trim());
            setInlineCommentContent("");
            onCancelComment?.(); // Clear pending selection after adding
        }
    }, [inlineCommentContent, onAddComment, onCancelComment]);

    const handleInlineCommentCancel = useCallback(() => {
        setInlineCommentContent("");
        onCancelComment?.();
    }, [onCancelComment]);

    const handleInlineCommentKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            handleInlineCommentSubmit();
        } else if (e.key === "Escape") {
            handleInlineCommentCancel();
        }
    }, [handleInlineCommentSubmit, handleInlineCommentCancel]);

    // Ref to measure container width for inline form
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState<number | null>(null);

    useEffect(() => {
        if (!containerRef.current || !pendingCommentSelection) return;
        const updateWidth = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.clientWidth);
            }
        };
        updateWidth();
        const observer = new ResizeObserver(updateWidth);
        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, [pendingCommentSelection]);

    // Find the index where we should show the inline comment form
    const inlineCommentAfterIndex = useMemo(() => {
        if (!pendingCommentSelection) return null;
        // Find the line with the endLine number
        for (const line of codeLines) {
            const lineNumber = line.kind === LineChangeKind.Removed ? line.originalLineNumber : line.newLineNumber;
            if (lineNumber === pendingCommentSelection.endLine) {
                return line.index;
            }
        }
        return null;
    }, [pendingCommentSelection, codeLines]);

    return (
        <div ref={containerRef} className={cn("grid grid-cols-[auto_auto_minmax(min-content,1fr)] overflow-auto", selectable && "select-none")} data-tracking-id="git-diff-view">
            {codeLines.map((line) => {
                const lineNumber = line.kind === LineChangeKind.Removed ? line.originalLineNumber : line.newLineNumber;
                const lineComments = getCommentsForLine(lineNumber);
                const showInlineForm = inlineCommentAfterIndex === line.index;
                return (
                    <React.Fragment key={line.key}>
                        <GitDiffViewLine
                            line={line}
                            selectable={selectable}
                            selected={isLineSelected(line.index)}
                            onLineMouseDown={handleLineMouseDown}
                            onLineMouseEnter={handleLineMouseEnter}
                            onLineClick={handleLineClick}
                            comments={lineComments}
                            onCommentClick={onCommentClick}
                        />
                        {showInlineForm && (
                            <div
                                className="sticky left-0 col-span-3 border-y border-border-base bg-surface-secondary p-3"
                                style={{ width: containerWidth ? `${containerWidth}px` : "100%" }}
                            >
                                <textarea
                                    value={inlineCommentContent}
                                    onChange={(e) => setInlineCommentContent(e.target.value)}
                                    onKeyDown={handleInlineCommentKeyDown}
                                    placeholder="Add your comment..."
                                    className="min-h-[60px] w-full resize-none rounded-md border border-border-base bg-surface-primary p-2 text-sm text-content-primary placeholder:text-content-muted focus:border-content-link focus:outline-none focus:ring-1 focus:ring-content-link"
                                    rows={2}
                                    autoFocus
                                />
                                <div className="mt-2 flex justify-end gap-2">
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={handleInlineCommentCancel}
                                        data-tracking-id="cancel-inline-comment"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        size="sm"
                                        onClick={handleInlineCommentSubmit}
                                        disabled={!inlineCommentContent.trim()}
                                        data-tracking-id="add-inline-comment"
                                    >
                                        Add comment
                                    </Button>
                                </div>
                            </div>
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

type GitDiffViewLineProps = {
    line: LineChange;
    selectable?: boolean;
    selected?: boolean;
    onLineMouseDown?: (line: LineChange, event: React.MouseEvent) => void;
    onLineMouseEnter?: (line: LineChange) => void;
    onLineClick?: (line: LineChange, event: React.MouseEvent) => void;
    comments?: ReviewComment[];
    onCommentClick?: (comment: ReviewComment) => void;
};

const GitDiffViewLine: FC<GitDiffViewLineProps> = ({ line, selectable = false, selected = false, onLineMouseDown, onLineMouseEnter, onLineClick, comments = [], onCommentClick }) => {
    let lineNumber: string;
    switch (line.kind) {
        case LineChangeKind.Removed:
            lineNumber = line.originalLineNumber.toString();
            break;
        case LineChangeKind.Unchanged:
        case LineChangeKind.Added:
            lineNumber = line.newLineNumber.toString();
            break;
        case LineChangeKind.Break:
            lineNumber = "-";
            break;
    }

    const handleMouseDown = (event: React.MouseEvent) => {
        if (selectable && onLineMouseDown && line.kind !== LineChangeKind.Break) {
            onLineMouseDown(line, event);
        }
    };

    const handleMouseEnter = () => {
        if (selectable && onLineMouseEnter && line.kind !== LineChangeKind.Break) {
            onLineMouseEnter(line);
        }
    };

    const handleClick = (event: React.MouseEvent) => {
        if (selectable && onLineClick) {
            onLineClick(line, event);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (!selectable || line.kind === LineChangeKind.Break) return;
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            if (onLineClick) {
                onLineClick(line, event as unknown as React.MouseEvent);
            }
        }
    };

    const hasComments = comments.length > 0;
    // Get the first comment for this line (to navigate to)
    const firstComment = comments[0];

    const handleCommentIndicatorClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        if (firstComment && onCommentClick) {
            onCommentClick(firstComment);
        }
    };

    const isSelectable = selectable && line.kind !== LineChangeKind.Break;

    return (
        <>
            {isSelectable ? (
                <button
                    type="button"
                    className={cn(
                        lineNumberClass({ kind: line.kind, selected, hasComment: hasComments }),
                        "hover:bg-surface-hover cursor-pointer focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-content-link focus-visible:outline-none",
                    )}
                    onMouseDown={handleMouseDown}
                    onMouseEnter={handleMouseEnter}
                    onClick={handleClick}
                    onKeyDown={handleKeyDown}
                    aria-label={`Select line ${lineNumber}`}
                    data-tracking-id="diff-line-number-click"
                >
                    {lineNumber}
                </button>
            ) : (
                <span
                    className={lineNumberClass({ kind: line.kind, selected, hasComment: hasComments })}
                    data-tracking-id="diff-line-number"
                >
                    {lineNumber}
                </span>
            )}
            {/* Comment indicator column */}
            <span className={cn(
                "flex w-6 items-center justify-center border-r border-r-border-base",
                hasComments ? "bg-surface-warning-subtle" : "bg-surface-secondary",
                line.kind === LineChangeKind.Break && "bg-surface-muted border-r-0",
            )}>
                {hasComments && line.kind !== LineChangeKind.Break && (
                    <Tooltip content={`${comments.length} comment${comments.length > 1 ? "s" : ""} - click to view`}>
                        <button
                            onClick={handleCommentIndicatorClick}
                            aria-label={`View ${comments.length} comment${comments.length > 1 ? "s" : ""}`}
                            className="flex size-4 items-center justify-center rounded-full bg-surface-warning text-content-warning hover:bg-surface-warning-hover focus-visible:ring-2 focus-visible:ring-content-link focus-visible:outline-none"
                            data-tracking-id="diff-comment-indicator-click"
                        >
                            <IconInfo size="sm" className="size-3" />
                        </button>
                    </Tooltip>
                )}
            </span>
            <pre
                className={cn(
                    lineContentClass({ kind: line.kind, selected, hasComment: hasComments }),
                    selectable && line.kind !== LineChangeKind.Break && "cursor-pointer hover:bg-surface-hover",
                )}
                onClick={handleClick}
                data-tracking-id="diff-line-content-click"
            >
                {line.line}
            </pre>
        </>
    );
};

export { GitDiffView };
